/**
 * `ChatShell` — the Ask Flint orchestrator. Lives directly under the
 * route `/ask/$chatId` and stitches the smaller chat components
 * together:
 *
 *   ┌───────────────────────────┬─────────────────────────────────────┐
 *   │                           │ ChatHeader (title + actions)        │
 *   │                           ├─────────────────────────────────────┤
 *   │ ThreadSidebar             │ ChatConversation                    │
 *   │ (saved chats + search)    │ (transcript, scroll-pin, thinking)  │
 *   │                           ├─────────────────────────────────────┤
 *   │                           │ ChatComposer (input + queue)        │
 *   └───────────────────────────┴─────────────────────────────────────┘
 *
 * The shell holds the always-on chrome (sidebar, threads index) and
 * mounts a fresh `ChatBody` once the per-thread loader resolves.
 * `ChatBody` is keyed on `chatId` so it remounts on thread switches
 * — that's load-bearing: `useChat`'s `messages` prop is only honoured
 * when its internal Chat instance is *constructed*, so we must let
 * the body unmount + remount so it constructs with the freshly
 * loaded transcript instead of the empty default.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { WEB_ENV } from "../../lib/env.ts";
import { useThreads } from "./hooks/use-threads.ts";
import { useThreadLoader } from "./hooks/use-thread-loader.ts";
import { useTitleTypewriter } from "./hooks/use-title-typewriter.ts";
import { ThreadSidebar } from "./thread-sidebar.tsx";
import { ChatHeader } from "./chat-header.tsx";
import { ChatConversation } from "./chat-conversation.tsx";
import { ChatComposer } from "./chat-composer.tsx";
import { takePendingPrompt } from "./pending-chat.ts";
import { ThinkingMessage } from "../../components/ai-messages/thinking-message.tsx";
import type { ThreadSummary } from "./types.ts";
import "../../components/ai-messages/ai-messages.css";

interface ChatShellProps {
  chatId: string;
}

export function ChatShell({ chatId }: ChatShellProps) {
  const navigate = useNavigate();
  const { threads, setThreads, refresh } = useThreads();
  const loader = useThreadLoader(chatId);

  const handleNewChat = useCallback(() => {
    void navigate({ to: "/ask" });
  }, [navigate]);

  const handleSelectThread = useCallback(
    (id: string) => {
      void navigate({ to: "/ask/$chatId", params: { chatId: id } });
    },
    [navigate],
  );

  const handleThreadDeleted = useCallback(
    (id: string) => {
      setThreads((current) => current.filter((t) => t.id !== id));
      if (id === chatId) handleNewChat();
    },
    [chatId, setThreads, handleNewChat],
  );

  return (
    <div className="flint-ai-chat-surface flex h-full min-h-0 flex-1 overflow-hidden">
      <ThreadSidebar
        activeId={chatId}
        threads={threads}
        onSelect={handleSelectThread}
        onNewChat={handleNewChat}
        onThreadDeleted={handleThreadDeleted}
      />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-paper">
        {loader.loading ? (
          <LoadingBody />
        ) : (
          <ChatBody
            key={chatId}
            chatId={chatId}
            initialMessages={loader.initialMessages}
            initialTitle={loader.initialTitle ?? "New Chat"}
            initialIcon={loader.initialIcon}
            setThreads={setThreads}
            refreshThreads={refresh}
            onNewChat={handleNewChat}
          />
        )}
      </div>
    </div>
  );
}

interface ChatBodyProps {
  chatId: string;
  initialMessages: UIMessage[];
  initialTitle: string;
  initialIcon: string | null;
  setThreads: React.Dispatch<React.SetStateAction<ThreadSummary[]>>;
  refreshThreads: () => Promise<void>;
  onNewChat: () => void;
}

/**
 * `ChatBody` owns `useChat` and everything that depends on it
 * (transcript, composer, title animation). It is keyed by `chatId`
 * in the parent so each thread switch (or fresh-page-load resolving
 * the loader) mounts a fresh instance with the right `messages`
 * prop. The `useChat` Chat instance is built once on mount; we don't
 * try to swap it in place.
 */
function ChatBody({
  chatId,
  initialMessages,
  initialTitle,
  initialIcon,
  setThreads,
  refreshThreads,
  onNewChat,
}: ChatBodyProps) {
  const router = useRouter();
  const [icon, setIcon] = useState<string | null>(initialIcon);
  const title = useTitleTypewriter(initialTitle);

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${WEB_ENV.VITE_FLINT_API_URL}/api/ai/chat`,
        credentials: "include",
      }),
    [],
  );

  const { messages, sendMessage, stop, status, error } = useChat<UIMessage>({
    id: chatId,
    transport,
    messages: initialMessages,
    onData: (part) => {
      if (isTitleDataPart(part)) {
        title.setTarget(part.data);
        setThreads((current) => {
          const exists = current.some((thread) => thread.id === chatId);
          const now = new Date().toISOString();
          if (exists) {
            return current.map((thread) =>
              thread.id === chatId ? { ...thread, title: part.data, updatedAt: now } : thread,
            );
          }
          return [{ id: chatId, title: part.data, icon: null, createdAt: now, updatedAt: now }, ...current];
        });
      } else if (isIconDataPart(part)) {
        setIcon(part.data);
        setThreads((current) =>
          current.map((thread) =>
            thread.id === chatId ? { ...thread, icon: part.data } : thread,
          ),
        );
      }
    },
    onFinish: () => {
      void refreshThreads();
    },
  });

  // If the user arrived from the home screen (or the launcher omnibox)
  // with a pre-typed prompt, it was stashed via `stashPendingPrompt`.
  // Read + clear it on first mount so the chat auto-submits without the
  // user having to retype. The ref guard ensures once per mount.
  const pendingSubmittedRef = useRef(false);
  useEffect(() => {
    if (pendingSubmittedRef.current) return;
    const pending = takePendingPrompt(chatId);
    if (!pending) return;
    pendingSubmittedRef.current = true;
    void sendMessage({ text: pending } as never);
  }, [chatId, sendMessage]);

  const isLoading = status === "submitted" || status === "streaming";
  const isWaitingForAssistant =
    status === "submitted" ||
    (isLoading && (messages.length === 0 || messages[messages.length - 1]?.role === "user"));

  const handleSubmit = useCallback(
    (payload: { text: string; files: NonNullable<Parameters<typeof sendMessage>[0]>["files"] }) => {
      void sendMessage({ text: payload.text, files: payload.files } as never);
    },
    [sendMessage],
  );

  return (
    <section className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-paper">
      <div className="pointer-events-none absolute inset-0 bg-grid-pattern bg-grid-fade text-slate-400/[0.10]" />

      <ChatHeader
        title={
          title.isAnimating ? (
            <>
              {title.displayed}
              <span className="animate-pulse">|</span>
            </>
          ) : (
            title.displayed
          )
        }
        icon={icon}
        onRefresh={() => void refreshThreads()}
        onClear={() => {
          // Clearing a chat == starting a new one. Easier mental model
          // than "wipe in place", because the server keeps the original
          // thread as an append-only record.
          void router.invalidate();
          onNewChat();
        }}
      />

      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <ChatConversation
          messages={messages}
          isLoading={isLoading}
          loadingThread={false}
          onSelectSuggestion={(prompt) => void sendMessage({ text: prompt })}
        />

        {isLoading ? (
          <div className="flex-none">
            <div className="mx-auto w-full max-w-full px-3 pb-1 pt-0.5 lg:max-w-3xl lg:px-4 xl:max-w-4xl">
              <ThinkingMessage
                message={isWaitingForAssistant ? "Flint is thinking" : "Flint is working"}
              />
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mx-auto mb-3 w-full max-w-full px-3 lg:max-w-3xl lg:px-4 xl:max-w-4xl">
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
              {error.message}
            </div>
          </div>
        ) : null}

        <ChatComposer
          status={status as "submitted" | "streaming" | "ready" | "error"}
          loadingThread={false}
          onSubmit={handleSubmit}
          onStop={stop}
        />
      </div>
    </section>
  );
}

function LoadingBody() {
  return (
    <section className="relative flex min-h-0 min-w-0 items-center justify-center bg-paper">
      <p className="text-sm text-muted">Loading chat…</p>
    </section>
  );
}

function isTitleDataPart(part: unknown): part is { type: "data-title"; data: string } {
  return isDataPart(part, "data-title");
}

function isIconDataPart(part: unknown): part is { type: "data-icon"; data: string } {
  return isDataPart(part, "data-icon");
}

function isDataPart(part: unknown, type: string): part is { type: string; data: string } {
  return (
    typeof part === "object" &&
    part !== null &&
    "type" in part &&
    (part as { type: unknown }).type === type &&
    "data" in part &&
    typeof (part as { data: unknown }).data === "string"
  );
}
